import Vue from 'vue'
import api from '@/api/client'
import { siteConfig, applyRuntimeSiteConfig } from '@/config/site'
import { navItems as defaultNavItems } from '@/data/categories'
import { applyGrayscaleMode } from '@/utils/grayscale'

/** 升版本号以丢弃旧 session 缓存（曾含过期联系邮箱等） */
const CACHE_KEY = 'site_config_cache_v3'
const CACHE_TTL = 60 * 1000
const REVALIDATE_MS = 30 * 1000

const state = Vue.observable({
  ready: false,
  loaded: false,
  commentSettings: {
    enabled: true,
    requireApproval: false
  },
  lastFetchedAt: 0
})

let fetchPromise = null
let visibilityBound = false

function updateFavicon(url) {
  if (!url) return
  let link = document.querySelector('link[rel="icon"]')
  if (!link) {
    link = document.createElement('link')
    link.rel = 'icon'
    document.head.appendChild(link)
  }
  link.href = url
}

function updateThemeColor(color) {
  if (!color) return
  let meta = document.querySelector('meta[name="theme-color"]')
  if (!meta) {
    meta = document.createElement('meta')
    meta.name = 'theme-color'
    document.head.appendChild(meta)
  }
  meta.content = color
}

function normalizeNavItems(items) {
  if (!Array.isArray(items) || !items.length) return defaultNavItems
  const filtered = items.filter(item => item.path !== '/about')
  const hasTools = filtered.some(item => item.path === '/tools')
  if (!hasTools) {
    filtered.push({ path: '/tools', label: '工具' })
  }
  return filtered.length ? filtered : defaultNavItems
}

function applySiteConfig(site) {
  if (!site || typeof site !== 'object') return
  const config = { ...site }
  config.navItems = normalizeNavItems(config.navItems)
  applyRuntimeSiteConfig(config)
  updateFavicon(config.faviconUrl)
  updateThemeColor(config.themeColor)
  applyGrayscaleMode()
}

function loadCachedSiteConfig() {
  try {
    const raw = sessionStorage.getItem(CACHE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    if (!parsed?.data || !parsed?.ts || Date.now() - parsed.ts > CACHE_TTL) {
      return null
    }
    return parsed.data
  } catch {
    return null
  }
}

function saveCachedSiteConfig(site) {
  try {
    sessionStorage.setItem(CACHE_KEY, JSON.stringify({ data: site, ts: Date.now() }))
  } catch {
    // ignore quota errors
  }
}

export function clearSiteConfigCache() {
  try {
    sessionStorage.removeItem(CACHE_KEY)
    sessionStorage.removeItem('site_config_cache_v1')
    sessionStorage.removeItem('site_config_cache_v2')
  } catch {
    // ignore
  }
  state.lastFetchedAt = 0
  state.loaded = false
}

function bindVisibilityRevalidate() {
  if (visibilityBound || typeof document === 'undefined') return
  visibilityBound = true
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      refreshSiteSettings({ silent: true }).catch(() => {})
    }
  })
}

export function bootstrapSiteSettings() {
  const cached = loadCachedSiteConfig()
  if (cached) {
    applySiteConfig(cached)
  } else {
    siteConfig.navItems = defaultNavItems
    applyGrayscaleMode()
  }
}

/**
 * @param {{ force?: boolean, silent?: boolean }} [opts]
 */
export async function refreshSiteSettings(opts = {}) {
  const force = !!opts.force
  const now = Date.now()
  if (!force && state.lastFetchedAt && now - state.lastFetchedAt < REVALIDATE_MS) {
    return siteConfig
  }
  if (fetchPromise) return fetchPromise

  fetchPromise = (async () => {
    try {
      const res = await api.getSiteConfig({ cache: 'no-store' })
      const site = res.data.site || {}
      applySiteConfig(site)
      saveCachedSiteConfig(site)
      if (res.data.commentSettings) {
        state.commentSettings = {
          enabled: res.data.commentSettings.enabled !== false,
          requireApproval: !!res.data.commentSettings.requireApproval
        }
      }
      state.loaded = true
      state.lastFetchedAt = Date.now()
      return siteConfig
    } catch (e) {
      if (!opts.silent && !loadCachedSiteConfig()) {
        siteConfig.navItems = defaultNavItems
      }
      if (opts.silent) return siteConfig
      throw e
    } finally {
      fetchPromise = null
      state.ready = true
    }
  })()

  return fetchPromise
}

export async function initSiteSettings() {
  bindVisibilityRevalidate()
  try {
    await refreshSiteSettings({ force: true })
  } catch {
    if (!loadCachedSiteConfig()) {
      siteConfig.navItems = defaultNavItems
    }
    state.ready = true
  }
}

export const siteSettings = {
  get ready() {
    return state.ready
  },

  get config() {
    return siteConfig
  },

  get navItems() {
    return siteConfig.navItems && siteConfig.navItems.length
      ? siteConfig.navItems
      : defaultNavItems
  },

  get toolsAccess() {
    const access = siteConfig.toolsAccess
    if (!access || typeof access !== 'object') {
      return { mode: 'free', requireLogin: [] }
    }
    return access
  },

  get commentSettings() {
    return state.commentSettings
  }
}

export default siteSettings
